/**
 * AdminEsims — Admin dashboard for eSIM operations:
 * - KPIs (orders, revenue, margin, ARPU, churn)
 * - Breakdown by language and by destination country
 * - Order table with force-cancel & refund
 * - Re-sync packages button
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Wifi, RefreshCw, AlertTriangle, TrendingUp, Users, Globe, DollarSign, BarChart3, ArrowLeft, Search, XCircle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'sonner';

const Kpi = ({ icon: Icon, label, value, accent = 'purple' }) => (
  <div className="bg-white/5 border border-white/10 rounded-xl p-4">
    <div className="flex items-center justify-between mb-2">
      <Icon className={`w-4 h-4 text-${accent}-300`} />
      <span className="text-[10px] uppercase tracking-wider text-white/50 font-bold">{label}</span>
    </div>
    <div className="text-2xl font-bold text-white">{value}</div>
  </div>
);

const AdminEsims = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');

  const loadStats = useCallback(async () => {
    try {
      const res = await axios.get('/api/admin/esim/stats');
      setStats(res.data);
    } catch (e) {
      toast.error('Stats unavailable');
    }
  }, []);

  const loadOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '200' });
      if (statusFilter) params.set('status', statusFilter);
      const res = await axios.get(`/api/admin/esim/orders?${params.toString()}`);
      setOrders(res.data?.orders || []);
      setTotal(res.data?.total || 0);
    } catch {
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  const filteredOrders = orders.filter((o) => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return (
      (o.user?.username || '').toLowerCase().includes(q) ||
      (o.user?.name || '').toLowerCase().includes(q) ||
      (o.user?.email || '').toLowerCase().includes(q) ||
      (o.user_id || '').toLowerCase().includes(q) ||
      (o.iccid || '').toLowerCase().includes(q) ||
      (o.package_snapshot?.country_title || '').toLowerCase().includes(q) ||
      (o.id || '').toLowerCase().includes(q)
    );
  });

  useEffect(() => {
    loadStats();
  }, [loadStats]);
  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const sync = async () => {
    setSyncing(true);
    try {
      await axios.post('/api/admin/esim/sync?force=true');
      toast.success(t('esim.syncSuccess'));
      await loadStats();
    } catch {
      toast.error('Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const cancelOrder = async (orderId) => {
    if (!window.confirm(t('esim.cancelConfirm'))) return;
    try {
      await axios.post(`/api/admin/esim/orders/${orderId}/cancel`);
      toast.success(t('esim.cancelDone'));
      await loadOrders();
      await loadStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Cancel failed');
    }
  };

  return (
    <div className="min-h-screen bg-black text-white pb-24" data-testid="admin-esims-page">
      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-2">
          <button
            type="button"
            onClick={() => navigate('/admin')}
            className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition"
            data-testid="admin-esims-back-btn"
            aria-label="Retour"
          >
            <ArrowLeft className="w-5 h-5 text-white" />
          </button>
          <span className="text-sm text-white/60">Admin</span>
        </div>
        <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 flex items-center justify-center">
              <Wifi className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">{t('esim.adminTitle')}</h1>
              <p className="text-xs text-white/60">{t('esim.adminSubtitle')}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {stats && (
              <>
                <span className="text-[10px] uppercase tracking-wider text-white/50 font-bold px-2 py-1 rounded-full border border-white/10 bg-white/5">
                  {t('esim.environment')}: {stats.environment}
                </span>
                <span className="text-[10px] uppercase tracking-wider text-white/50 font-bold px-2 py-1 rounded-full border border-white/10 bg-white/5">
                  {t('esim.markup')}: +{stats.markup_percent}%
                </span>
              </>
            )}
            <button
              type="button"
              onClick={sync}
              disabled={syncing}
              className="bg-white/10 hover:bg-white/20 text-white text-xs font-semibold px-4 py-2 rounded-xl flex items-center gap-2 transition disabled:opacity-50"
              data-testid="admin-esim-sync-btn"
              title="Airalo exige au moins une synchronisation par heure — une tâche automatique tourne aussi en arrière-plan toutes les 60 minutes."
            >
              <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
              {syncing ? t('esim.syncing') : t('esim.syncNow')}
            </button>
            <span className="text-[10px] text-emerald-300 px-2 py-1 rounded-full bg-emerald-500/15 border border-emerald-400/25 flex items-center gap-1" data-testid="admin-esim-autosync-badge" title="Sync automatique horaire active">
              <RefreshCw className="w-3 h-3" /> Auto-sync 1h
            </span>
          </div>
        </div>

        {/* KPIs */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mb-6" data-testid="admin-esim-kpis">
            <Kpi icon={Users} label={t('esim.kpiTotalOrders')} value={stats.total_orders} />
            <Kpi icon={Wifi} label={t('esim.kpiActive')} value={stats.active_orders} accent="emerald" />
            <Kpi icon={AlertTriangle} label={t('esim.kpiCancelled')} value={stats.cancelled_orders} accent="red" />
            <Kpi icon={RefreshCw} label={t('esim.kpiTopups')} value={stats.topups_count} />
            <Kpi icon={DollarSign} label={t('esim.kpiRevenue')} value={`$${stats.gross_revenue_usd?.toFixed(2)}`} accent="emerald" />
            <Kpi icon={TrendingUp} label={t('esim.kpiMargin')} value={`$${stats.margin_usd?.toFixed(2)}`} />
            <Kpi icon={BarChart3} label={t('esim.kpiARPU')} value={`$${stats.arpu_usd?.toFixed(2)}`} />
            <Kpi icon={AlertTriangle} label={t('esim.kpiFailed')} value={stats.failed_orders} accent="red" />
          </div>
        )}

        {/* Breakdowns */}
        {stats && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
            <div className="bg-white/5 border border-white/10 rounded-xl p-4">
              <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
                <Users className="w-4 h-4" /> {t('esim.byLanguage')}
              </h3>
              {stats.by_language.length === 0 ? (
                <div className="text-white/40 text-xs">—</div>
              ) : (
                <ul className="space-y-2">
                  {stats.by_language.map((row) => (
                    <li key={row.language} className="flex items-center justify-between text-xs">
                      <span className="text-white/80 uppercase font-mono">{row.language}</span>
                      <span className="text-white/60">
                        {row.count} · <span className="text-emerald-300 font-semibold">${row.revenue_usd?.toFixed(2)}</span>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="bg-white/5 border border-white/10 rounded-xl p-4">
              <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
                <Globe className="w-4 h-4" /> {t('esim.byCountry')}
              </h3>
              {stats.by_country.length === 0 ? (
                <div className="text-white/40 text-xs">—</div>
              ) : (
                <ul className="space-y-2 max-h-64 overflow-y-auto">
                  {stats.by_country.map((row) => (
                    <li key={(row.country_code || 'na') + (row.country_title || '')} className="flex items-center justify-between text-xs">
                      <span className="text-white/80">{row.country_title || row.country_code || '—'}</span>
                      <span className="text-white/60">
                        {row.count} · <span className="text-emerald-300 font-semibold">${row.revenue_usd?.toFixed(2)}</span>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}

        {/* Orders table */}
        <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between flex-wrap gap-2">
            <h3 className="text-sm font-bold text-white">
              {t('esim.ordersTable')} <span className="text-white/40 font-normal">({filteredOrders.length}/{total})</span>
            </h3>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/40" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={t('common.search') || 'Search…'}
                  className="bg-white/10 border border-white/15 rounded-lg pl-7 pr-3 py-1 text-xs text-white placeholder-white/40 w-44 focus:outline-none focus:border-purple-400/40"
                  data-testid="admin-esim-search-input"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="bg-white/10 border border-white/15 rounded-lg px-2 py-1 text-xs text-white"
                data-testid="admin-esim-status-filter"
              >
                <option value="">— {t('esim.filterAll')} —</option>
                <option value="active">active</option>
                <option value="paid">paid</option>
                <option value="cancelled">cancelled</option>
                <option value="pending_payment">pending_payment</option>
                <option value="airalo_failed">airalo_failed</option>
              </select>
            </div>
          </div>
          {loading ? (
            <div className="p-6 text-center text-white/50 text-sm">Loading…</div>
          ) : filteredOrders.length === 0 ? (
            <div className="p-6 text-center text-white/50 text-sm">—</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-white/5">
                  <tr className="text-left text-white/50 uppercase tracking-wider">
                    <th className="px-3 py-2">{t('esim.user')}</th>
                    <th className="px-3 py-2">{t('esim.package')}</th>
                    <th className="px-3 py-2">{t('esim.price')}</th>
                    <th className="px-3 py-2">{t('esim.status')}</th>
                    <th className="px-3 py-2">{t('esim.iccid')}</th>
                    <th className="px-3 py-2 text-right">{t('esim.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOrders.map((o) => {
                    const snap = o.package_snapshot || {};
                    const canCancel = ['active', 'paid'].includes(o.status) && !o.refunded;
                    return (
                      <tr key={o.id} className="border-t border-white/5" data-testid={`admin-esim-row-${o.id}`}>
                        <td className="px-3 py-2 text-white">
                          {o.user?.username || o.user?.name || o.user_id?.slice(0, 8)}
                          {o.user?.is_banned && <span className="ml-1 text-red-300">⛔</span>}
                          {o.user_language && <div className="text-[10px] text-white/40 uppercase">{o.user_language}</div>}
                        </td>
                        <td className="px-3 py-2 text-white">
                          <div className="font-semibold">{snap.country_title || '—'}</div>
                          <div className="text-[10px] text-white/50">{snap.data_label} · {snap.validity_days}j</div>
                        </td>
                        <td className="px-3 py-2 text-emerald-300 font-bold">
                          ${(o.retail_price_usd || 0).toFixed(2)}
                          {o.refunded && <div className="text-[10px] text-red-300 font-normal">refunded</div>}
                        </td>
                        <td className="px-3 py-2"><span className="text-white/80">{o.status}</span></td>
                        <td className="px-3 py-2 font-mono text-[10px] text-white/60">{o.iccid?.slice(-8) || '—'}</td>
                        <td className="px-3 py-2 text-right">
                          {canCancel ? (
                            <button
                              type="button"
                              onClick={() => cancelOrder(o.id)}
                              className="px-2 py-1 rounded bg-red-500/20 hover:bg-red-500/30 text-red-300 text-[10px] font-bold flex items-center gap-1 ml-auto"
                              data-testid={`admin-esim-cancel-${o.id}`}
                              title={t('esim.forceCancel')}
                            >
                              <XCircle className="w-3 h-3" />
                              {t('esim.forceCancel')}
                            </button>
                          ) : (
                            <span className="text-[10px] text-white/30">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminEsims;
